import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { motion, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import {
  HomeIcon,
  CalendarIcon,
  UserIcon,
  NewspaperIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  CalendarIcon as CalendarIconSolid,
  UserIcon as UserIconSolid,
  NewspaperIcon as NewspaperIconSolid,
  TicketIcon as TicketIconSolid,
} from '@heroicons/react/24/solid';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import CategoryList from './pages/CategoryList';
import BusinessList from './pages/BusinessList';
import BusinessDetail from './pages/BusinessDetail';
import SearchResults from './pages/SearchResults';
import Profile from './pages/Profile';
import Login from './pages/LoginPage';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import About from './pages/About';
import LogoutPage from './pages/LogoutPage';
import EventsList from './pages/EventsList';
import EventDetail from './pages/EventDetail';
import CouponsList from './pages/CouponsList';
import CouponDetail from './pages/CouponDetail';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingCheck from './components/auth/OnboardingCheck';
import EditProfilePage from './pages/EditProfilePage';
import ScanQR from './pages/ScanQR';
import Jobs from './pages/Jobs';
import Ranking from './pages/Ranking';
import TransactionsPage from './pages/TransactionsPage';

const queryClient = new QueryClient();

interface NavItem {
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  activeIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
}

// Bottom Navigation Component
const BottomNav: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { user } = useAuth();

  const navItems: NavItem[] = [
    { path: '/', icon: HomeIcon, activeIcon: HomeIconSolid, label: 'Início' },
    { path: '/news', icon: NewspaperIcon, activeIcon: NewspaperIconSolid, label: 'Notícias' },
    { path: '/coupons', icon: TicketIcon, activeIcon: TicketIconSolid, label: 'Promo' },
    { path: '/events', icon: CalendarIcon, activeIcon: CalendarIconSolid, label: 'Eventos' },
    { path: user ? '/profile' : '/login', icon: UserIcon, activeIcon: UserIconSolid, label: user ? 'Perfil' : 'Entrar' },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 md:hidden">
      <div className="flex justify-around items-center h-16">
        {navItems.map(({ path, icon: Icon, activeIcon: ActiveIcon, label }) => {
          const isActive = currentPath === path;
          const IconComponent = isActive ? ActiveIcon : Icon;
          return (
            <a
              key={path}
              href={path}
              className={`flex flex-col items-center justify-center flex-1 h-full ${
                isActive ? 'text-primary' : 'text-gray-600'
              }`}
            >
              <IconComponent className="h-6 w-6" />
              <span className="text-xs mt-1">{label}</span>
            </a>
          );
        })}
      </div>
    </nav>
  );
};

// Add this type definition after the imports
const AnimatePresenceWrapper = AnimatePresence as React.ComponentType<{
  children: React.ReactNode;
  mode?: "wait" | "sync" | "popLayout";
}>;

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="h-screen bg-background flex flex-col overflow-hidden">
            {/* Desktop Navbar */}
            <div className="hidden md:block">
              <Navbar />
            </div>
            
            {/* Main Content */}
            <main className="flex-1 overflow-hidden">
              <div className="h-full max-w-md mx-auto px-4 py-4 md:max-w-none md:px-6 md:py-6">
                <LazyMotion features={domAnimation}>
                  <AnimatePresenceWrapper mode="wait">
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/categories" element={<CategoryList />} />
                      <Route path="/category/:categoryId" element={<BusinessList />} />
                      <Route path="/businesses" element={<BusinessList />} />
                      <Route path="/business/:id" element={<BusinessDetail />} />
                      <Route path="/search" element={<SearchResults />} />
                      <Route path="/events" element={<EventsList />} />
                      <Route path="/events/:id" element={<EventDetail />} />
                      <Route path="/coupons" element={<CouponsList />} />
                      <Route path="/coupons/:id" element={<CouponDetail />} />
                      <Route path="/jobs" element={<Jobs />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/onboarding" element={
                        <ProtectedRoute>
                          <OnboardingPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/profile" element={
                        <ProtectedRoute>
                          <OnboardingCheck>
                            <Profile />
                          </OnboardingCheck>
                        </ProtectedRoute>
                      } />
                      <Route path="/edit-profile" element={
                        <ProtectedRoute>
                          <EditProfilePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/scan-qr" element={
                        <ProtectedRoute>
                          <ScanQR />
                        </ProtectedRoute>
                      } />
                      <Route path="/ranking" element={
                        <ProtectedRoute>
                          <Ranking />
                        </ProtectedRoute>
                      } />
                      <Route path="/news" element={<News />} />
                      <Route path="/news/:id" element={<NewsDetail />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/logout" element={<LogoutPage />} />
                      <Route path="/transactions" element={
                        <ProtectedRoute>
                          <TransactionsPage />
                        </ProtectedRoute>
                      } />
                    </Routes>
                  </AnimatePresenceWrapper>
                </LazyMotion>
              </div>
            </main>

            {/* Mobile Bottom Navigation */}
            <BottomNav />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App; 